'use client';
import { ListItemInformation } from '@/app/ui/components/list/listitem/listItemInformation';
import { mapProductsToEcommerceItems, trackSSTEvent } from '@/app/utils/tracking';
import React, { useEffect, useRef, useState } from 'react';
import { DeviceType, IProduct } from '@mybestbrands/types';
import { ListItemRetailerLink } from '@/app/ui/components/list/listitem/listItemRetailerLink';
import { ListItemProductImage } from '@/app/ui/components/list/listitem/listItemProductImage';
import { useIsPaidTraffic, useIsSeoClickOutEnabled } from '@/app/helpers/hooks/useIsSeoClickOutEnabled';
import ClickOutCTA from '@/app/ui/components/clickOutCTA';
import ProductCardOverlay from '@/app/ui/components/list/listitem/productcardoverlay';
import ListItemMultipleImages from '@/app/ui/components/list/listitem/listitemmultipleimages/listitemmultipleimages';
import { PrefetchOnHoverLink } from '@/app/ui/components/prefetchonhoverlink';
import CouponOverlay from '@/app/ui/components/list/listitem/couponoverlay';
import useElementInViewport from '@/app/helpers/hooks/useElementInViewport';
import { useDispatch } from 'react-redux';
import { setProductImpressions } from '@/app/lib/redux';
import { FeatureToggles } from '@/app/featuretoggles/ft';

interface Props {
  item: IProduct;
  locale: string;
  pdpUrl?: string;
  plpUrl?: string;
  clickEvent?: string;
  defaultPdpOverlayEnabled?: boolean;
  isOverlay?: boolean;
  deviceType?: DeviceType;
  groupProducts?: IProduct[];
  isRelatedProduct?: boolean;
  ft?: FeatureToggles;
  onRemove?: () => void;
  apiUrl?: string;
}

export const GetListItemContent = ({
  item,
  locale,
  clickEvent,
  pdpUrl,
  isOverlay,
  groupProducts,
  isRelatedProduct,
  ft,
  deviceType,
  onRemove,
  apiUrl,
}: Props) => {
  const [quickView, showQuickView] = useState(false);
  const [couponView, setCouponView] = useState(false);
  const isSeoClickOutEnabled = useIsSeoClickOutEnabled(item);
  const isPaidTraffic = useIsPaidTraffic();
  const listItemRef = useRef<HTMLDivElement>(null);
  const isInViewport = useElementInViewport(listItemRef, { threshold: 0.8, observeOnce: true });
  const dispatch = useDispatch();

  useEffect(() => {
    if (isInViewport) {
      dispatch(setProductImpressions(item.productId));
    }
  }, [isInViewport]);

  let detailsUrl = '';
  const scroll = true;
  if (locale !== 'it') {
    detailsUrl = pdpUrl || '';

    //if (defaultPdpOverlayEnabled) {
    //detailsUrl = plpUrl || '';
    //scroll = false;
    //}
  }

  const trackingData = {
    currency: 'EUR',
    value: item.epc / 100,
    items: mapProductsToEcommerceItems([item]),
  };

  const handleClick = () => {
    showQuickView(!quickView);
    trackSSTEvent('product_card_quick_view_display', trackingData);
  };
  const handleCouponClick = () => {
    if (isSeoClickOutEnabled) {
      setCouponView(!couponView);
    } else {
      showQuickView(!quickView);
      trackSSTEvent('product_card_quick_view_display', trackingData);
    }
  };

  const handleOutsideClick = (event: MouseEvent) => {
    if (listItemRef.current && !listItemRef.current.contains(event.target as Node)) {
      showQuickView(false);
      setCouponView(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleOutsideClick);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, []);

  const getContent = () => {
    //sold out products are not shown on PLP anymore
    // if (!item.clickOutAllowed) {
    //   if (detailsUrl) {
    //     // unavailable product with PDP
    //     return (
    //       <Link
    //         href={detailsUrl}
    //         className={'text-black text-decoration-none'}
    //         title={`${item.brandName} ${item.name}`}
    //         rel={'follow'}
    //         scroll={scroll}>
    //         <ListItemProductImage
    //           ftNewImageFormat={newImageFormat}
    //           item={item}
    //           locale={locale}
    //           trackingData={trackingData || null}
    //         />
    //         <ListItemInformation item={item} trackingData={trackingData || null} />
    //       </Link>
    //     );
    //   }
    //
    //   // unavailable product without PDP
    //   return (
    //     <>
    //       <ListItemProductImage
    //         ftNewImageFormat={newImageFormat}
    //         item={item}
    //         locale={locale}
    //         trackingData={trackingData || null}
    //       />
    //       <ListItemInformation item={item} trackingData={trackingData || null} />
    //     </>
    //   );
    // }

    // direct clickout
    if (locale === 'it' || isOverlay) {
      return (
        <ClickOutCTA product={item} locale={locale}>
          <ListItemProductImage item={item} locale={locale} trackingData={trackingData || null} />
          <ListItemInformation item={item} />
          <ListItemRetailerLink item={item} trackingData={trackingData || null} />
        </ClickOutCTA>
      );
    }

    if (isPaidTraffic) {
      return (
        <div onClick={handleClick} className={'cursor-pointer position-relative'}>
          <ListItemMultipleImages
            item={item}
            locale={locale}
            trackingData={trackingData || null}
            isRelatedProduct={isRelatedProduct}
            groupProducts={groupProducts}
            deviceType={deviceType}
            ft={ft}
            onWishListRemove={onRemove}
            apiUrl={apiUrl}
          />
          <PrefetchOnHoverLink
            href={detailsUrl}
            className={'text-black text-decoration-none'}
            title={`${item.brandName} ${item.name}`}
            rel={'follow'}
            scroll={scroll}
            onClick={(event) => {
              event.stopPropagation();
            }}>
            <ListItemInformation item={item} />
          </PrefetchOnHoverLink>
          <ListItemRetailerLink item={item} trackingData={trackingData || null} />
          {quickView && (
            <ProductCardOverlay
              onClose={() => showQuickView(false)}
              item={item}
              locale={locale}
              detailsUrl={pdpUrl || ''}
              groupProducts={groupProducts}
            />
          )}
        </div>
      );
    }

    // clickout on retailer link and image
    if (isSeoClickOutEnabled) {
      return (
        <>
          <ClickOutCTA product={item} locale={locale}>
            <ListItemMultipleImages
              item={item}
              locale={locale}
              trackingData={trackingData || null}
              onCouponClick={() => handleCouponClick()}
              groupProducts={groupProducts}
              isRelatedProduct={isRelatedProduct}
              deviceType={deviceType}
              ft={ft}
              onWishListRemove={onRemove}
              apiUrl={apiUrl}
            />
          </ClickOutCTA>
          <PrefetchOnHoverLink
            href={detailsUrl}
            className={'text-black text-decoration-none'}
            title={`${item.brandName} ${item.name}`}
            rel={'follow'}
            scroll={scroll}>
            <ListItemInformation item={item} />
          </PrefetchOnHoverLink>
          <ClickOutCTA product={item} locale={locale}>
            <ListItemRetailerLink item={item} trackingData={trackingData || null} />
          </ClickOutCTA>
          {couponView && <CouponOverlay onClose={() => setCouponView(false)} item={item} locale={locale} />}
        </>
      );
    }

    // quick view and multiple images for direct traffic
    return (
      <div onClick={handleClick} className={'cursor-pointer position-relative'}>
        <ListItemMultipleImages
          item={item}
          locale={locale}
          trackingData={trackingData || null}
          onCouponClick={() => handleCouponClick()}
          groupProducts={groupProducts}
          isRelatedProduct={isRelatedProduct}
          deviceType={deviceType}
          ft={ft}
          onWishListRemove={onRemove}
          apiUrl={apiUrl}
        />
        <PrefetchOnHoverLink
          href={detailsUrl}
          className={'text-black text-decoration-none'}
          title={`${item.brandName} ${item.name}`}
          rel={'follow'}
          scroll={scroll}
          onClick={(event) => {
            event.preventDefault(); // Disable the link click
          }}>
          <ListItemInformation item={item} />
        </PrefetchOnHoverLink>
        <ListItemRetailerLink item={item} trackingData={trackingData || null} />
        {quickView && (
          <ProductCardOverlay
            onClose={() => showQuickView(false)}
            item={item}
            locale={locale}
            detailsUrl={pdpUrl || ''}
            groupProducts={groupProducts}
          />
        )}
      </div>
    );
  };

  return (
    <div
      className="d-flex flex-column justify-content-start position-relative"
      style={{ fontSize: '0.9rem' }}
      onClick={() => {
        if (clickEvent) trackSSTEvent(clickEvent, trackingData);
      }}>
      <div ref={listItemRef}>{getContent()}</div>
    </div>
  );
};
